import { memo, useState } from 'react';

import { Box, Divider, ZillowLogo } from '@zillow/constellation-10';
import {
  IconArchiveFilled,
  IconBankFilled,
  IconDollarSignCircleFilled,
  IconEditFormFilled,
  IconHammerFilled,
  IconHomeTypeFilled,
  IconMessageFilled,
  IconTrendingFilled,
  IconUserFilled,
  IconUserGroupFilled,
} from '@zillow/constellation-icons';

import { PathsManifest } from '@zg-rentals/paths-manifest';
import { useRentalManagerUserTrackingWebApiHandler_event } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';
import { RentalManagerUserTrackingEvent } from '@zg-rentals/ts-rental-manager-api-beta-client';

import { getNavDisplays, getPaymentsPath } from '../../utils/displays';
import HeaderNavigationClickstreamEvents from '../../utils/clickstream';
import { CSTriggerSourceFromHeaderTitlesMap, HeaderTitles } from '../../constants';

import AccountMenu from './AccountMenu';
import AdaptiveAnchor from '../AdaptiveAnchor';
import LogoDesktop from '../Logo/LogoDesktop';
import NavigationItem from '../NavigationItem';
import NotificationCenter from '../NotificationCenter';
import PlusIconPropertyMenu from '../PlusIconPropertyMenu';

import type { Props as HeaderNavigationProps } from '../HeaderNavigation';
import type { User } from '../../types';

export type Props = Partial<HeaderNavigationProps>;

const VerticalNavigation = (props: Props) => {
  const [isAlertDropdownOpen, setIsAlertDropdownOpen] = useState(false);
  const [hasNewNotifications, setHasNewNotifications] = useState(false);

  const { displays } = getNavDisplays(props);

  const {
    /**
     * altLogo true - zrm logo
     * altLogo false - zillow logo
     */
    altLogo = true,
    brand = 'zillow',
    pathname = '',
    reactRouterLink: v4Link,
    userModel = {} as User,
    webSocketInstance = null,
    escapeAllLinks = false,
    accountSettingsEnabled = false,
  } = props;

  const { features = {}, loggedIn, paymentsAcceptedCount, hasSeenNewPaymentsPage } = userModel;
  const { canViewClaimFeedListing: canAccessAddFeedListingsModal = false, canViewOnboardingFlow = false } =
    features as User['features'];

  const {
    displayAnalytics,
    displayMainBilling,
    displayMainLeadManagement,
    displayMainLeads,
    displayMainMaintenanceTracker,
    displayMainMessages,
    displayMainMyProperties,
    displayMainNotificationCenter,
    displayMainPayments,
    displayOnboardingTasks,
    displayUserManagement,
  } = displays;

  // Pass link component from either react-router or next-js
  const Link = v4Link;

  const useAbsolutePaths = !props.useRelativePaths;
  const paths = new PathsManifest('/rental-manager', brand, useAbsolutePaths, props.host).getAllPaths();

  const {
    adManagementPage,
    analyticsOverview,
    analyticsPage,
    billingDue,
    billingPage,
    inboxPage,
    leadManagementPage,
    leadsPage,
    maintenanceTrackerPage,
    payments,
    paymentsAggregatePage,
    paymentsGetStartedPage,
    propertiesPage,
    rentPayment,
    rentPaymentPage,
    setupTasks,
    adManagementTabsPage,
    userManagementPage,
  } = paths;

  const { mutate: trackEvent } = useRentalManagerUserTrackingWebApiHandler_event();

  const handleLinkClick = (pageClickingTo: string) => {
    if (loggedIn && userModel) {
      const csLink = CSTriggerSourceFromHeaderTitlesMap[pageClickingTo];

      if (pageClickingTo === HeaderTitles.payments && hasSeenNewPaymentsPage === false) {
        trackEvent({ getParams: { trackingEvent: RentalManagerUserTrackingEvent.OPEN_NEW_PAYMENTS_PAGE } });
      }

      if (csLink) {
        HeaderNavigationClickstreamEvents.ClickNavigationLink({ user: userModel, link: csLink });
      }
    }
  };

  const handleClickPropertyMenuItem = (event: React.SyntheticEvent) => {
    // prevent parent navigation
    event.stopPropagation();

    handleLinkClick(HeaderTitles.myProperties);
  };

  const isPropertiesPageActive = pathname.includes(propertiesPage);
  const isPaymentPageActive = pathname.includes(payments) || pathname.includes(rentPayment);

  return (
    <Box
      data-testid="VerticalNavigation"
      css={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%', padding: '200' }}
    >
      {/* logo */}
      <Box css={{ padding: '{spacing.800} 0 {spacing.800} {spacing.200}' }}>
        <AdaptiveAnchor
          path={propertiesPage}
          escape={escapeAllLinks}
          useAbsolutePaths={useAbsolutePaths}
          link={Link}
          title="Return to properties page"
        >
          <Box css={{ width: altLogo ? '200px' : '154px', height: 'auto' }}>
            {altLogo ? <LogoDesktop alternate={true} /> : <ZillowLogo showLegacyLogo={true} />}
          </Box>
        </AdaptiveAnchor>
      </Box>

      <Box
        css={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          marginBottom: '200',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {/* properties */}
        {loggedIn && displayMainMyProperties && (
          <NavigationItem
            isActive={isPropertiesPageActive}
            title={HeaderTitles.myProperties}
            escape={escapeAllLinks}
            path={propertiesPage}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconHomeTypeFilled />}
            InactiveIcon={<IconHomeTypeFilled />}
            TrailingIcon={
              <PlusIconPropertyMenu
                Link={Link}
                canAccessAddFeedListingsModal={canAccessAddFeedListingsModal}
                escapeAllLinks={escapeAllLinks}
                handleNoFeedIconPlusClick={handleLinkClick}
                onClickPropertyMenuItem={handleClickPropertyMenuItem}
                propertiesPage={propertiesPage}
                useAbsolutePaths={useAbsolutePaths}
                userModel={userModel}
              />
            }
            onClick={() => {
              handleLinkClick(HeaderTitles.myProperties);
            }}
          />
        )}

        {/* leads */}
        {displayMainLeadManagement && (
          <NavigationItem
            isActive={pathname.includes(leadManagementPage)}
            title={HeaderTitles.leadManagement}
            escape={true}
            path={leadManagementPage}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconUserGroupFilled />}
            InactiveIcon={<IconUserGroupFilled />}
            TrailingIcon={null}
            newTagCookieName="hasSeenLeadManagement"
            showNewTag={!canViewOnboardingFlow}
            onClick={() => {
              handleLinkClick(HeaderTitles.leadManagement);
            }}
          />
        )}

        {/* messages */}
        {displayMainMessages && (
          <NavigationItem
            isActive={pathname.includes(inboxPage)}
            title={HeaderTitles.messages}
            escape={true}
            path={inboxPage}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconMessageFilled />}
            InactiveIcon={<IconMessageFilled />}
            TrailingIcon={null}
            onClick={() => {
              handleLinkClick(HeaderTitles.messages);
            }}
          />
        )}

        {/* maintenance */}
        {displayMainMaintenanceTracker && (
          <NavigationItem
            isActive={pathname.includes(maintenanceTrackerPage)}
            title={HeaderTitles.maintenanceTracker}
            escape={true}
            path={maintenanceTrackerPage}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconHammerFilled />}
            InactiveIcon={<IconHammerFilled />}
            TrailingIcon={null}
            onClick={() => {
              handleLinkClick(HeaderTitles.maintenanceTracker);
            }}
          />
        )}

        {/* contacts */}
        {displayMainLeads && (
          <NavigationItem
            isActive={pathname.includes(leadsPage)}
            title={HeaderTitles.leads}
            escape={true}
            path={leadsPage}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconMessageFilled />}
            InactiveIcon={<IconMessageFilled />}
            TrailingIcon={null}
            onClick={() => {
              handleLinkClick(HeaderTitles.leads);
            }}
          />
        )}

        {/* analytics */}
        {displayAnalytics && (
          <NavigationItem
            isActive={pathname.includes(analyticsPage)}
            title={HeaderTitles.analytics}
            escape={true}
            path={`${analyticsPage}/${analyticsOverview}`}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconTrendingFilled />}
            InactiveIcon={<IconTrendingFilled />}
            TrailingIcon={null}
            onClick={() => {
              handleLinkClick(HeaderTitles.analytics);
            }}
          />
        )}

        {/* billing */}
        {displayMainBilling && (
          <NavigationItem
            isActive={pathname.includes(billingPage)}
            title={HeaderTitles.billing}
            escape={true}
            path={`${billingPage}/${billingDue}`}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconBankFilled />}
            InactiveIcon={<IconBankFilled />}
            TrailingIcon={null}
            onClick={() => {
              handleLinkClick(HeaderTitles.billing);
            }}
          />
        )}

        {/* user management */}
        {displayUserManagement && (
          <NavigationItem
            isActive={pathname.includes(userManagementPage)}
            title={HeaderTitles.userManagement}
            escape={true}
            path={userManagementPage}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconUserFilled />}
            InactiveIcon={<IconUserFilled />}
            TrailingIcon={null}
            onClick={() => {
              handleLinkClick(HeaderTitles.userManagement);
            }}
          />
        )}

        {/* ad management */}
        {loggedIn && userModel.isEnterpriseCustomer && (
          <NavigationItem
            isActive={pathname.includes(adManagementTabsPage)}
            title={HeaderTitles.adManagement}
            escape={true}
            path={`${adManagementPage}/${adManagementTabsPage}`}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconArchiveFilled />}
            InactiveIcon={<IconArchiveFilled />}
            TrailingIcon={null}
            onClick={() => {
              handleLinkClick(HeaderTitles.adManagement);
            }}
          />
        )}

        {/* payments */}
        {loggedIn && displayMainPayments && (
          <NavigationItem
            isActive={isPropertiesPageActive ? false : isPaymentPageActive}
            title={HeaderTitles.payments}
            escape={true}
            path={getPaymentsPath({
              paymentsAcceptedCount,
              paymentsAggregatePage,
              paymentsGetStartedPage,
              rentPaymentPage,
            })}
            link={Link}
            useAbsolutePaths={useAbsolutePaths}
            ActiveIcon={<IconDollarSignCircleFilled />}
            InactiveIcon={<IconDollarSignCircleFilled />}
            onClick={() => {
              handleLinkClick(HeaderTitles.payments);
            }}
          />
        )}

        {/* alerts */}
        {loggedIn && displayMainNotificationCenter && (
          <NavigationItem
            isActive={false}
            title={HeaderTitles.alerts}
            escape={true}
            path={null}
            link={Link}
            InactiveIcon={
              <Box css={{ marginRight: '200' }}>
                <NotificationCenter
                  brand={brand}
                  useLeftNav={true}
                  isOpen={isAlertDropdownOpen}
                  onClose={() => setIsAlertDropdownOpen(false)}
                  webSocketInstance={webSocketInstance}
                  onHasNewNotificationsChange={setHasNewNotifications}
                />
              </Box>
            }
            TrailingIcon={
              hasNewNotifications && (
                <Box
                  css={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    marginRight: '100',
                    height: '200',
                    width: '200',
                    borderRadius: 'ellipse',
                    backgroundColor: 'text.feedback.warning.hero',
                  }}
                />
              )
            }
            onClick={(event: React.MouseEvent<HTMLElement, MouseEvent> | undefined) => {
              if (event) {
                // do not navigate anywhere
                event.preventDefault();
                event.stopPropagation();
              }

              handleLinkClick(HeaderTitles.alerts);

              // control the dropdown open state so that the entire item is clickable
              if (isAlertDropdownOpen === false) {
                setIsAlertDropdownOpen(true);
              }

              if (isAlertDropdownOpen === true) {
                // use a slight delay when closing the dropdown
                setTimeout(() => {
                  setIsAlertDropdownOpen(false);
                }, 100);
              }
            }}
          />
        )}

        {displayOnboardingTasks && (
          <>
            <Divider css={{ marginBlock: '400' }} />

            {/* onboarding tasks */}
            <NavigationItem
              isActive={pathname.includes(setupTasks)}
              title={HeaderTitles.onboardingTasks}
              escape={true}
              path={`${adManagementPage}/${setupTasks}`}
              link={Link}
              useAbsolutePaths={useAbsolutePaths}
              ActiveIcon={<IconEditFormFilled />}
              InactiveIcon={<IconEditFormFilled />}
              TrailingIcon={null}
              onClick={() => {
                handleLinkClick(HeaderTitles.onboardingTasks);
              }}
            />
          </>
        )}
      </Box>

      <AccountMenu
        displays={displays}
        paths={paths}
        userModel={userModel}
        accountSettingsEnabled={accountSettingsEnabled}
      />
    </Box>
  );
};

export default memo(VerticalNavigation);
